// Display · DateValue
//
// One-place date / timestamp renderer. Reads `useFormat()` so locale
// switches are picked up automatically, handles null / empty with a
// fallback string, and optionally prepends a muted calendar icon for
// editorial table cells.
//
// Variants:
//   - `"date"` (default) — locale-formatted date (YYYY-MM-DD in pl-PL).
//   - `"timestamp"`     — locale-formatted date + time.

import React from "react";
import { Space } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import { SPACE, TEXT } from "@ds/tokens/palette";
import { useFormat } from "@ds/display/useNumberFormat";
import { withExportSpec } from "@ds/display/exportSpec";

// ─── Inputs ───────────────────────────────────────────────────────────────

export interface DateValueInput {
  /** ISO string, Date, or null. Empty string is treated as null. */
  value: string | Date | null | undefined;
  /** "date" (default) or "timestamp" (date + time). */
  variant?: "date" | "timestamp";
  /** Prepend a muted calendar icon. Default false. */
  withIcon?: boolean;
  /** Text shown for null / empty values. Default "—". */
  fallback?: string;
  /** `data-testid` on the root. Defaults to `"date-value"`. */
  testId?: string;
}

export type DateValueProps = DateValueInput;

// ─── Component ────────────────────────────────────────────────────────────

export function DateValue({
  value,
  variant = "date",
  withIcon = false,
  fallback = "—",
  testId = "date-value",
}: DateValueProps) {
  const fmt = useFormat();
  if (value == null || value === "") {
    return <span data-testid={testId}>{fallback}</span>;
  }
  const formatted =
    variant === "timestamp"
      ? fmt.dateTime(value)
      : fmt.date(value);
  if (!withIcon) {
    return <span data-testid={testId}>{formatted}</span>;
  }
  return (
    <Space size={SPACE.xs} data-testid={testId}>
      <CalendarOutlined style={{ color: TEXT.muted }} />
      <span>{formatted}</span>
    </Space>
  );
}

// ─── Excel export ─────────────────────────────────────────────────────────
//
// A date cell exports as a native Date so Excel can sort, filter by period and
// group it. The `timestamp` variant carries a time of day, so it asks for a
// format with hours — which is also what tells the coercion layer to preserve
// the instant instead of collapsing to the calendar day.

withExportSpec<typeof DateValue, DateValueProps>(DateValue, {
  type: "date",
  value: (p) => p.value,
  numFmt: (p) => (p.variant === "timestamp" ? "yyyy-mm-dd hh:mm" : "yyyy-mm-dd"),
});
